import React, { useEffect, useState } from 'react';

interface GoogleMapsLoaderProps {
  apiKey: string;
  children: React.ReactNode;
  onLoad?: () => void;
}

declare global {
  interface Window {
    initGoogleMapsCallback?: () => void;
  }
}

const GoogleMapsLoader: React.FC<GoogleMapsLoaderProps> = ({ apiKey, children, onLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    console.log('[GoogleMapsLoader] useEffect triggered', {
      hasApiKey: !!apiKey,
      apiKeyLength: apiKey?.length,
      apiKeyPrefix: apiKey?.substring(0, 10) + '...',
      hasWindowGoogle: !!window.google,
      hasWindowGoogleMaps: !!window.google?.maps,
      hasWindowGoogleMapsPlaces: !!window.google?.maps?.places,
      isLoaded
    });

    // Helper function to check if Places library is ready
    const checkPlacesReady = (): boolean => {
      const hasPlaces = !!(window.google?.maps?.places);
      if (!hasPlaces && window.google?.maps) {
        // Debug: log what IS available
        console.log('[GoogleMapsLoader] Places not ready, but Maps is. Available properties:', {
          mapsKeys: Object.keys(window.google.maps),
          hasAutocomplete: window.google.maps.places ? ('Autocomplete' in window.google.maps.places) : false,
          mapsType: typeof window.google.maps,
          placesType: typeof window.google.maps.places
        });
      }
      return hasPlaces;
    };

    // Track timeout IDs for cleanup
    const placesCheckTimeouts: NodeJS.Timeout[] = [];

    // Helper function to wait for Places and then set loaded
    const waitForPlacesAndSetLoaded = () => {
      let attempts = 0;
      const maxAttempts = 20; // 10 seconds max (20 * 500ms)
      
      const checkPlaces = () => {
        attempts++;
        console.log(`[GoogleMapsLoader] Checking for Places library (attempt ${attempts}/${maxAttempts})`);
        
        // Every 5 attempts, log detailed diagnostic info
        if (attempts % 5 === 0) {
          console.log('[GoogleMapsLoader] 🔍 Diagnostic check:', {
            hasGoogle: !!window.google,
            hasMaps: !!window.google?.maps,
            hasPlaces: !!window.google?.maps?.places,
            mapsKeys: window.google?.maps ? Object.keys(window.google.maps).slice(0, 10) : [],
            scriptExists: !!document.querySelector('#google-maps-script'),
            allGoogleScripts: Array.from(document.querySelectorAll('script[src*="maps.googleapis.com"]')).length
          });
        }
        
        if (checkPlacesReady()) {
          console.log('[GoogleMapsLoader] ✅ Places library is now available!');
          setIsLoaded(true);
          if (onLoad) {
            console.log('[GoogleMapsLoader] Calling onLoad callback');
            onLoad();
          }
        } else if (attempts < maxAttempts) {
          console.log(`[GoogleMapsLoader] ⏳ Places library not ready yet, retrying in 500ms...`);
          const timeoutId = setTimeout(checkPlaces, 500);
          placesCheckTimeouts.push(timeoutId);
        } else {
          console.error('[GoogleMapsLoader] ❌ Places library failed to load after max attempts');
          console.error('[GoogleMapsLoader] 🔍 Final diagnostic:', {
            hasGoogle: !!window.google,
            hasMaps: !!window.google?.maps,
            hasPlaces: !!window.google?.maps?.places,
            mapsKeys: window.google?.maps ? Object.keys(window.google.maps) : [],
            scriptInDOM: !!document.querySelector('#google-maps-script'),
            scriptSrc: document.querySelector('#google-maps-script')?.getAttribute('src')?.substring(0, 100),
            callbackExists: typeof window.initGoogleMapsCallback === 'function'
          });
          console.error('[GoogleMapsLoader] 💡 Possible issues:');
          console.error('  1. API key missing Places API permission');
          console.error('  2. API key invalid or expired');
          console.error('  3. Network/CORS blocking the script');
          console.error('  4. Script loaded but Places library not initialized');
          console.error('  5. Check Network tab for Google Maps API request errors');
          // Still set loaded to true to prevent infinite loading, but log the issue
          setIsLoaded(true);
          if (onLoad) {
            onLoad();
          }
        }
      };
      
      checkPlaces();
    };

    // If Google Maps API is already loaded, check for Places
    if (window.google && window.google.maps) {
      console.log('[GoogleMapsLoader] ✅ Google Maps already loaded, checking Places library');
      console.log('[GoogleMapsLoader] Checking Places library:', {
        hasPlaces: !!window.google.maps.places,
        placesType: typeof window.google.maps.places
      });
      
      // Even if Maps is loaded, Places might need a moment to initialize
      // Give it a small delay (like BookOnline does) before checking
      setTimeout(() => {
        if (checkPlacesReady()) {
          console.log('[GoogleMapsLoader] ✅ Places library is ready');
          setIsLoaded(true);
          if (onLoad) {
            console.log('[GoogleMapsLoader] Calling onLoad callback');
            onLoad();
          }
        } else {
          console.log('[GoogleMapsLoader] ⏳ Maps loaded but Places not ready after delay, waiting...');
          waitForPlacesAndSetLoaded();
        }
      }, 100); // Same delay as BookOnline uses
      return;
    }

    console.log('[GoogleMapsLoader] Google Maps not loaded, setting up script injection');

    // Check if script already exists
    const existingScript = document.querySelector('#google-maps-script') || 
                           document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.warn('[GoogleMapsLoader] ⚠️ Existing Google Maps script found:', {
        id: existingScript.id,
        src: (existingScript as HTMLScriptElement).src?.replace(apiKey, 'API_KEY_HIDDEN')
      });
      
      // If script exists but Places isn't ready, wait for it
      if (window.google?.maps && !window.google.maps.places) {
        console.log('[GoogleMapsLoader] Script exists but Places not ready, waiting...');
        waitForPlacesAndSetLoaded();
        return;
      }
    }

    // Create a global callback function
    // If callback already exists (from BookOnline), chain it
    const existingCallback = window.initGoogleMapsCallback;
    window.initGoogleMapsCallback = () => {
      console.log('[GoogleMapsLoader] ✅ initGoogleMapsCallback fired - Google Maps loaded');
      console.log('[GoogleMapsLoader] ⏱️ Callback fired at:', new Date().toISOString());
      
      // Call existing callback if it exists (from BookOnline or other components)
      if (existingCallback && typeof existingCallback === 'function') {
        console.log('[GoogleMapsLoader] Calling existing callback (chained)');
        try {
          existingCallback();
        } catch (e) {
          console.error('[GoogleMapsLoader] Error in existing callback:', e);
        }
      }
      
      // Check what was actually loaded
      const loadedLibraries = {
        hasGoogle: !!window.google,
        hasMaps: !!window.google?.maps,
        hasPlaces: !!window.google?.maps?.places,
        placesType: typeof window.google?.maps?.places,
        googleMapsKeys: window.google?.maps ? Object.keys(window.google.maps) : [],
        fullGoogleObject: window.google ? 'exists' : 'missing'
      };
      console.log('[GoogleMapsLoader] Verifying loaded libraries:', loadedLibraries);
      
      // Check if Places API is actually available (even if not in .places)
      if (window.google?.maps) {
        console.log('[GoogleMapsLoader] Available on google.maps:', {
          allKeys: Object.keys(window.google.maps),
          hasPlacesProperty: 'places' in window.google.maps,
          placesValue: window.google.maps.places,
          placesType: typeof window.google.maps.places
        });
        
        // Check if Autocomplete exists anywhere
        if (window.google.maps.places && 'Autocomplete' in window.google.maps.places) {
          console.log('[GoogleMapsLoader] ✅ Autocomplete class found in places');
        } else {
          console.warn('[GoogleMapsLoader] ⚠️ Autocomplete class NOT found');
        }
      }
      
      // Additional check: try to access Places directly
      try {
        const placesCheck = window.google?.maps?.places;
        console.log('[GoogleMapsLoader] Direct Places check:', {
          places: placesCheck,
          isUndefined: placesCheck === undefined,
          isNull: placesCheck === null,
          type: typeof placesCheck
        });
        
        // Try to see if Places is loaded but under a different path
        if (window.google?.maps && !placesCheck) {
          console.warn('[GoogleMapsLoader] ⚠️ Places library missing. This usually means:');
          console.warn('  1. Places API not enabled in Google Cloud Console');
          console.warn('  2. API key restrictions blocking Places API');
          console.warn('  3. Script URL missing libraries=places (but we checked this)');
        }
      } catch (e) {
        console.error('[GoogleMapsLoader] Error checking Places:', e);
      }
      
      // Wait for Places library to be available
      // Sometimes there's a small delay between Maps loading and Places being ready
      // Use a small delay (like BookOnline does) to give Places time to initialize
      setTimeout(() => {
        if (window.google?.maps?.places) {
          console.log('[GoogleMapsLoader] ✅ Places library is available after delay');
          setIsLoaded(true);
          if (onLoad) {
            console.log('[GoogleMapsLoader] Calling onLoad callback');
            onLoad();
          }
        } else {
          console.log('[GoogleMapsLoader] ⏳ Maps loaded but Places not available after delay, waiting...');
          console.log('[GoogleMapsLoader] Full window.google structure:', {
            google: window.google ? 'exists' : 'missing',
            maps: window.google?.maps ? 'exists' : 'missing',
            mapsKeys: window.google?.maps ? Object.keys(window.google.maps) : [],
            mapsPrototype: window.google?.maps ? Object.getPrototypeOf(window.google.maps) : null
          });
          waitForPlacesAndSetLoaded();
        }
      }, 100); // Same delay as BookOnline uses - gives Places time to initialize
    };

    // Create script element
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,drawing&callback=initGoogleMapsCallback`;
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;

    console.log('[GoogleMapsLoader] Creating script tag:', {
      id: script.id,
      url: scriptUrl.replace(apiKey, 'API_KEY_HIDDEN'),
      fullUrl: scriptUrl, // Log full URL for debugging (user can check in Network tab)
      async: script.async,
      defer: script.defer,
      hasCallback: scriptUrl.includes('callback='),
      hasPlacesLibrary: scriptUrl.includes('libraries=places'),
      urlParts: {
        hasKey: scriptUrl.includes('key='),
        hasLibraries: scriptUrl.includes('libraries='),
        hasPlaces: scriptUrl.includes('places'),
        hasCallback: scriptUrl.includes('callback=')
      }
    });
    
    // Verify the URL is correct
    if (!scriptUrl.includes('libraries=places')) {
      console.error('[GoogleMapsLoader] ❌ CRITICAL: Script URL missing libraries=places parameter!');
      console.error('[GoogleMapsLoader] Current URL:', scriptUrl.replace(apiKey, 'API_KEY_HIDDEN'));
    } else {
      console.log('[GoogleMapsLoader] ✅ Script URL correctly includes libraries=places');
    }

    // Add error handler
    script.onerror = (error) => {
      console.error('[GoogleMapsLoader] ❌ Script failed to load:', error);
      console.error('[GoogleMapsLoader] Script URL:', scriptUrl.replace(apiKey, 'API_KEY_HIDDEN'));
      console.error('[GoogleMapsLoader] Full URL (check Network tab):', scriptUrl);
      console.error('[GoogleMapsLoader] This could indicate:');
      console.error('  1. Invalid API key - Check Google Cloud Console');
      console.error('  2. API key missing Places API permission - Enable Places API');
      console.error('  3. Network/CORS issue - Check browser Network tab');
      console.error('  4. API quota exceeded - Check Google Cloud Console billing');
      console.error('  5. API key restrictions - Check API key settings in Google Cloud');
      console.error('');
      console.error('🔍 TO DIAGNOSE:');
      console.error('  1. Open Network tab in DevTools');
      console.error('  2. Filter by "maps.googleapis.com"');
      console.error('  3. Click on the failed request');
      console.error('  4. Check Response tab for error message');
      console.error('  5. Check Headers tab for status code');
    };

    // Add load handler to verify script loaded
    script.onload = () => {
      console.log('[GoogleMapsLoader] ✅ Script onload event fired');
      console.log('[GoogleMapsLoader] Checking if callback will fire...');
      console.log('[GoogleMapsLoader] 📋 DIAGNOSTIC: After script.onload, check:');
      console.log('  1. Network tab → Find the maps.googleapis.com request');
      console.log('  2. Check Response tab for any error messages');
      console.log('  3. Check if URL includes &libraries=places');
      console.log('  4. Verify status code is 200 (not 403, 400, etc.)');
      
      // The callback should fire automatically, but let's verify
      setTimeout(() => {
        if (!window.google?.maps) {
          console.warn('[GoogleMapsLoader] ⚠️ Script loaded but window.google.maps not available after 1 second');
          console.warn('[GoogleMapsLoader] This suggests the script loaded but Google Maps failed to initialize');
        } else if (!window.google.maps.places) {
          console.warn('[GoogleMapsLoader] ⚠️ Maps loaded but Places library missing');
          console.warn('[GoogleMapsLoader] This usually means:');
          console.warn('  - Places API not enabled for this API key');
          console.warn('  - API key restrictions blocking Places API');
          console.warn('  - Check Google Cloud Console → APIs & Services → Enabled APIs');
        }
      }, 1000);
    };

    // Add the script to the document
    console.log('[GoogleMapsLoader] Appending script to document.head');
    console.log('[GoogleMapsLoader] Current scripts in head:', 
      Array.from(document.head.querySelectorAll('script[src*="maps.googleapis.com"]')).map(s => ({
        id: s.id,
        src: (s as HTMLScriptElement).src?.replace(apiKey, 'API_KEY_HIDDEN')
      }))
    );
    document.head.appendChild(script);
    console.log('[GoogleMapsLoader] Script appended, waiting for callback...');
    console.log('[GoogleMapsLoader] Please check Network tab for:', scriptUrl.replace(apiKey, 'API_KEY_HIDDEN'));

    // Set up a retry mechanism in case the callback doesn't fire
    const retryTimeout = setTimeout(() => {
      console.log('[GoogleMapsLoader] Retry timeout reached (5 seconds)');
      if (window.google?.maps) {
        console.log('[GoogleMapsLoader] ✅ Google Maps loaded but callback not fired, checking Places...');
        console.log('[GoogleMapsLoader] Checking Places library:', {
          hasPlaces: !!window.google.maps.places
        });
        
        // Wait for Places library to be available
        if (window.google.maps.places) {
          console.log('[GoogleMapsLoader] ✅ Places library is available');
          setIsLoaded(true);
          if (onLoad) {
            console.log('[GoogleMapsLoader] Calling onLoad callback');
            onLoad();
          }
        } else {
          console.log('[GoogleMapsLoader] ⏳ Maps loaded but Places not ready, waiting...');
          waitForPlacesAndSetLoaded();
        }
      } else {
        console.error('[GoogleMapsLoader] ❌ Google Maps failed to load within timeout, retrying...');
        console.error('[GoogleMapsLoader] Current state:', {
          hasGoogle: !!window.google,
          hasMaps: !!window.google?.maps,
          scriptExists: !!document.querySelector('#google-maps-script')
        });
        // Remove script and try again
        const oldScript = document.querySelector('#google-maps-script');
        if (oldScript) {
          console.log('[GoogleMapsLoader] Removing old script and retrying');
          oldScript.remove();
        }
        document.head.appendChild(script);
      }
    }, 5000);

    // Cleanup function
    return () => {
      console.log('[GoogleMapsLoader] Cleanup: removing callback and clearing timeouts');
      if (window.initGoogleMapsCallback) {
        delete window.initGoogleMapsCallback;
      }
      clearTimeout(retryTimeout);
      // Clear all Places check timeouts
      placesCheckTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      placesCheckTimeouts.length = 0;
    };
  }, [apiKey, onLoad]);

  console.log('[GoogleMapsLoader] Render:', { isLoaded });

  // Expose diagnostic function to window for manual testing
  useEffect(() => {
    (window as any).diagnoseGoogleMaps = () => {
      console.log('🔍 GOOGLE MAPS DIAGNOSTIC REPORT');
      console.log('================================');
      console.log('');
      
      console.log('1. API Key Check:');
      console.log('   - API Key present:', !!apiKey);
      console.log('   - API Key length:', apiKey?.length);
      console.log('   - API Key prefix:', apiKey?.substring(0, 10) + '...');
      console.log('');
      
      console.log('2. Script Tags:');
      const scripts = Array.from(document.querySelectorAll('script[src*="maps.googleapis.com"]'));
      console.log('   - Number of Google Maps scripts:', scripts.length);
      scripts.forEach((script, i) => {
        const src = (script as HTMLScriptElement).src;
        console.log(`   - Script ${i + 1}:`, {
          id: script.id || '(no id)',
          src: src.replace(apiKey, 'API_KEY_HIDDEN'),
          hasPlaces: src.includes('libraries=places'),
          hasCallback: src.includes('callback=')
        });
      });
      console.log('');
      
      console.log('3. Window.google Status:');
      console.log('   - window.google exists:', !!window.google);
      console.log('   - window.google.maps exists:', !!window.google?.maps);
      console.log('   - window.google.maps.places exists:', !!window.google?.maps?.places);
      if (window.google?.maps) {
        console.log('   - Maps API keys (first 20):', Object.keys(window.google.maps).slice(0, 20));
      }
      console.log('');
      
      console.log('4. Network Request Check:');
      console.log('   - Open Network tab → Filter "maps.googleapis.com"');
      console.log('   - Look for request with status 200 (success) or error code');
      console.log('   - Check Response tab for any error messages');
      console.log('');
      
      console.log('5. API Key Configuration:');
      console.log('   - Go to: https://console.cloud.google.com/apis/credentials');
      console.log('   - Find your API key and verify:');
      console.log('     ✓ Maps JavaScript API is ENABLED');
      console.log('     ✓ Places API is ENABLED (CRITICAL!)');
      console.log('     ✓ No API restrictions blocking your domain');
      console.log('     ✓ Billing is enabled');
      console.log('');
      
      console.log('6. Test Places API Directly:');
      if (window.google?.maps?.places) {
        console.log('   ✅ Places library is available!');
        try {
          const testDiv = document.createElement('div');
          const testAutocomplete = new window.google.maps.places.Autocomplete(testDiv);
          console.log('   ✅ Autocomplete can be instantiated');
        } catch (e) {
          console.error('   ❌ Error creating Autocomplete:', e);
        }
      } else {
        console.log('   ❌ Places library NOT available');
        console.log('   This is the root cause of the issue.');
      }
      console.log('');
      
      return {
        hasApiKey: !!apiKey,
        scriptCount: scripts.length,
        hasGoogle: !!window.google,
        hasMaps: !!window.google?.maps,
        hasPlaces: !!window.google?.maps?.places
      };
    };
    
    console.log('[GoogleMapsLoader] 💡 Run window.diagnoseGoogleMaps() in console for full diagnostic');
    
    return () => {
      delete (window as any).diagnoseGoogleMaps;
    };
  }, [apiKey]);

  return <>{isLoaded ? children : <div className="h-full w-full flex items-center justify-center">Loading maps...</div>}</>;
};

export default GoogleMapsLoader; 